import { useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import { formatSerpText } from '@/utils/approval/serpDocument';
import {
  buildSerpReturnSummary,
  getSerpReturnBoxKeepYn,
  getSerpReturnItems,
  type SerpStoreReturnSource,
} from '@/utils/approval/serpStoreReturn';
import { SerpStoreReturnDetailButtonRow, SerpStoreReturnRoot } from './styles';

/** 레거시 작성·조회 화면의 「자세히 보기」 버튼·모달 id — 조회 화면이 이 id 로 포탈 버튼으로 바꿔 끼움 */
const DETAIL_BUTTON_ID = 'settlementList';
const LEGACY_DETAIL_MODAL_ID = 'settlementModal';

type TablesProps = {
  source: SerpStoreReturnSource;
  t: TFunction;
};

/** 레거시 #APV_SERP_SHOP_RETURN .form-group 의 표 2개(칸 구성 동일) */
const SerpStoreReturnTables = ({ source, t }: TablesProps) => {
  const summary = buildSerpReturnSummary(getSerpReturnItems(source));
  return (
    <>
      <label className="label font-weight-bold">{t('시즌 반품 정보')}</label>
      <table className="table table-bordered mb-30 text-center">
        <tbody>
          <tr>
            <th>{t('반품명')}</th>
            <td colSpan={3}>{formatSerpText(source.tit)}</td>
          </tr>
          <tr>
            <th>{t('반품요청번호')}</th>
            <td colSpan={3}>{formatSerpText(source.rtnReqNo)}</td>
          </tr>
          <tr>
            <th>{t('반품 유형')}</th>
            <td colSpan={3}>{formatSerpText(source.distTypNm)}</td>
          </tr>
          <tr>
            <th>{t('운영시즌')}</th>
            <td>{formatSerpText(source.operSsnCd)}</td>
            <th>{t('차수')}</th>
            <td>{formatSerpText(source.rtnDeg)}</td>
          </tr>
          <tr>
            <th>{t('반품시작일')}</th>
            <td>{formatSerpText(source.staDe)}</td>
            <th>{t('반품종료일')}</th>
            <td>{formatSerpText(source.endDe)}</td>
          </tr>
          <tr>
            <th>{t('반품박스 보관지시여부')}</th>
            <td colSpan={3}>
              {formatSerpText(getSerpReturnBoxKeepYn(source))}
            </td>
          </tr>
        </tbody>
      </table>
      <table className="table table-bordered mb-30 text-center">
        <tbody>
          <tr>
            <th colSpan={4}>{t('시즌별 스타일/컬러 요약')}</th>
          </tr>
          <tr>
            <th>{t('상품시즌')}</th>
            <th>{t('스타일 갯수')}</th>
            <th>{t('컬러 갯수')}</th>
            <th />
          </tr>
          {summary.map((row) => (
            <tr key={row.ssnCd}>
              <td>{row.ssnCd}</td>
              <td>{row.prodCnt}</td>
              <td>{row.colorCnt}</td>
              <td />
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/** 작성 화면 — 저장 content 와 같은 표 + 「자세히 보기」 */
export const SerpStoreReturnTablesView = ({
  onOpenDetail,
  ...props
}: TablesProps & { onOpenDetail: () => void }) => (
  <SerpStoreReturnRoot>
    <div className="form-group">
      <SerpStoreReturnTables {...props} />
    </div>
    <SerpStoreReturnDetailButtonRow>
      <BoxButton type="button" variant="secondary" onClick={onOpenDetail}>
        {props.t('자세히보기')}
      </BoxButton>
    </SerpStoreReturnDetailButtonRow>
  </SerpStoreReturnRoot>
);

/** attributes.content — 버튼 자리는 조회 화면이 포탈 버튼으로 바꿔 그림(레거시 문서와 같은 id) */
export const buildSerpStoreReturnContentHtml = (
  source: SerpStoreReturnSource,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <div className="form-group">
      <SerpStoreReturnTables source={source} t={t} />
      <button type="button" id={DETAIL_BUTTON_ID}>
        {t('자세히보기')}
      </button>
    </div>,
  );

/** 저장 content(레거시 문서 포함) — 레거시 모달 틀(작성자가 열었던 상세 포함)은 빼고 버튼만 포탈 버튼으로 */
export const SerpStoreReturnSnapshot = ({
  content,
  onOpenDetail,
}: {
  content: string;
  onOpenDetail: () => void;
}) => {
  const { t } = useTranslation();
  const transform = useCallback<ElementTransform>(
    ({ element, props }) => {
      if (element.id === LEGACY_DETAIL_MODAL_ID) return null;
      if (element.id === DETAIL_BUTTON_ID) {
        return (
          <SerpStoreReturnDetailButtonRow key={props.key as string}>
            <BoxButton type="button" variant="secondary" onClick={onOpenDetail}>
              {t('자세히보기')}
            </BoxButton>
          </SerpStoreReturnDetailButtonRow>
        );
      }
      return undefined;
    },
    [onOpenDetail, t],
  );
  return (
    <SerpStoreReturnRoot>
      <SerpContentSnapshot content={content} transform={transform} />
    </SerpStoreReturnRoot>
  );
};
